import { useInterpret, useSelector } from '@xstate/react'
import { useTheme } from '@mui/material/styles'
import { InterpreterFrom } from 'xstate'
import * as Sentry from '@sentry/react'

import {
  createContext,
  useEffect,
  useMemo,
} from 'react'
import LmnDialogContent from 'src/components/Dialogs/Lmn/LmnDialogContent'
import { BaseDialog } from 'src/components/Dialogs/Base/BaseDialog'
import lmnMachine from 'src/machines/lmnMachine'
import { getLmnStep, getLoading } from 'src/helpers/lmnHelpers'
import mixpanel from 'mixpanel-browser'
import isSentryInitialized from './utils/isSentryInitialized'

interface LmnDialogProps {
  isOpen: boolean
  isFullScreen?: boolean
  onCancel: () => void
  merchantUuid: string
}

// Lmn Beluga Qualification Dialog Props used in the react context
// We are omitting the props that are only used in this file
interface LmnContextProps extends Omit<LmnDialogProps, 'isOpen' | 'onCancel'> { }

/*
  Setting up two variables in the react context
  1. The lmnQualificationMachine context
  2. All props that need to be stored globally, but not mutated (aka LmnContextProps)
*/
const LmnContext = createContext({
  lmnContextService: {} as InterpreterFrom<typeof lmnMachine>,
  lmnContextProps: {} as LmnContextProps,
})

const LmnDialog = ({
  isOpen,
  isFullScreen,
  onCancel,
  merchantUuid,
}: LmnDialogProps): JSX.Element => {
  const { palette } = useTheme()
  const lmnContextService = useInterpret(lmnMachine)

  // const lmnLoading = useSelector(
  //   lmnContextService,
  //   getLmnLoading,
  // )
  // const loadingMessage = lmnLoading ? 'Checking your eligibility...' : undefined

  const { send } = lmnContextService
  const loading = useSelector(lmnContextService, getLoading)
  const lmnStep = useSelector(lmnContextService, getLmnStep)

  const getLoadingMessage = (): string => {
    switch (lmnStep) {
      case 3:
        return 'Sending a code to your phone number...'
      case 4:
        return 'Verifying the code...'
      case 6:
        return 'Uploading your data...'
      default:
        return 'Loading...'
    }
  }

  const loadingMessage = getLoadingMessage()
  // const loadingAnimation = useSelector(lmnContextService, getLoadingAnimation)

  // const paymentsMachineState = useSelector(lmnContextService, getPaymentsMachineState)

  const handleClose = (_event: object, reason: string): void => {
    // Do not close unless you click on the X explicitly
    if (!['backdropClick', 'escapeKeyDown'].includes(reason)) {
      send('ON_CANCEL')
    }
  }

  // Wrapped in a useMemo hook to prevent unnecessary changes every render
  const lmnContextProps: LmnContextProps = useMemo(() => ({ merchantUuid }), [merchantUuid])

  // Wrapped in a useMemo hook to prevent unnecessary changes every render
  const lmnContextValue = useMemo(() => ({
    lmnContextService,
    lmnContextProps,
  }), [
    lmnContextService,
    lmnContextProps,
  ])

  useEffect(() => {
    if (isOpen) {
      if (!isSentryInitialized()) {
        Sentry.init({
          dsn: process.env.SENTRY_DSN,
          environment: process.env.ENVIRONMENT,
          tracesSampleRate: 1,
          debug: true,
          replaysOnErrorSampleRate: 1.0,
          replaysSessionSampleRate: 1.0,
          integrations: [
            Sentry.replayIntegration({
              maskAllText: false,
              blockAllMedia: false,
              maskAllInputs: true,
              minReplayDuration: 15000,
              mask: ['.sentry-mask'],
            }),
            Sentry.replayCanvasIntegration(),
            Sentry.httpClientIntegration({
              failedRequestStatusCodes: [[400, 599]],
            }),
          ],
          sendDefaultPii: true,
        })
      }
      // only run this once on component load
      send({
        type: 'ON_LOAD',
        data: {
          merchantUuid,
          onCancel,
        },
      })
      mixpanel.track('view_lmn_dialog')
    }
  }, [merchantUuid, isOpen, send, onCancel])

  // TODO: deal with loading complete
  // const onLoadingComplete = (): void => {
  //   send({ type: 'TOGGLE_LOADING_ANIMATION' })
  // }

  return (
    <BaseDialog
      backgroundColor={palette.gray5.main}
      // closeDisabled={paymentsMachineState === 'submittingOrder' || loadingAnimation}
      elementId="LmnDialog"
      isLmnDialog
      isFullScreen={isFullScreen}
      width={false}
      removePadding
      isOpen={isOpen}
      loading={loading}
      loadingMessage={loadingMessage}
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      // onLoadingComplete={loadingMessage ? () => {} : onLoadingComplete}
      onClose={handleClose}
    >
      <LmnContext.Provider value={lmnContextValue}>
        <LmnDialogContent />
      </LmnContext.Provider>
    </BaseDialog>
  )
}

export default LmnDialog

export {
  LmnContext,
  LmnDialogProps,
}
